import { useState } from 'react';
import { omit } from 'lodash';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import interviewsServices, {
  AddTechnicalValidationInterviewReview,
  DeleteInterviewReview,
  UpdateInterviewReview,
  UpdateTechnicalValidationInterviewReview,
} from '@/services/interviews';
import {
  InterviewReviewType,
  InterviewScoreEnum,
  InterviewType,
  TechnicalValidationCompetency,
} from '@/types/api';
import { getInputNumberedValue } from '@/utils/interviews';
import {
  convertGradingRubricIntoTechnicalValidationCompetencies,
  getScoreValueBasedOnMaxScoreValue,
} from '@/utils/technicalChallenge';
import TechnicalValidationReviewsManagerComponent, {
  CustomReviewType,
} from './TechnicalValidationReviewsManager.component';
import { formatCompetencies } from './TechnicalValidationReviewsManager.utils';
import AiOverviewModal from '../AiOverviewModal';

type TechnicalValidationReviewsManagerProps = {
  interview: InterviewType;
  canAddFirstReview?: boolean;
  onRefetchInterview: () => void;
};

export default function TechnicalValidationReviewsManager({
  interview,
  canAddFirstReview = false,
  onRefetchInterview,
}: TechnicalValidationReviewsManagerProps): JSX.Element {
  const { profile } = useProfile();
  const toast = useToastContext();

  const addInterviewReviewMutation = useApiSubmit<any, AddTechnicalValidationInterviewReview>(
    interviewsServices.addTechnicalValidationReviewInterview
  );

  const deleteInterviewReviewMutation = useApiSubmit<any, DeleteInterviewReview>(
    interviewsServices.deleteReviewInterview
  );

  const updateInterviewReviewMutation = useApiSubmit<any, UpdateTechnicalValidationInterviewReview>(
    interviewsServices.updateTechnicalValidationReviewInterview
  );

  const updateAiOverviewMutation = useApiSubmit<any, UpdateInterviewReview>(
    interviewsServices.updateReviewAiOverview
  );

  const [reviews, setReviews] = useState<CustomReviewType[]>(
    interview.reviews?.map((review) => ({
      ...omit(review, ['interview', 'score']),
      competencies: formatCompetencies({
        scores: review.score,
        competencies: review.competencies,
      }),
      isEditing: false,
      isExpanded: false,
    }))
  );

  const [aiOverviewModalState, setAiOverviewModalState] = useState<{
    isOpen: boolean;
    reviewId: string;
    overview: string;
  } | null>(null);

  const gradingRubricCompetencies = convertGradingRubricIntoTechnicalValidationCompetencies(
    interview.technicalChallenge?.technicalChallenge?.gradingRubric
  );

  const onAddNewReview = (): void => {
    setReviews((prev) =>
      prev.concat({
        note: '',
        competencies: gradingRubricCompetencies,
        user: { ...profile },
        isEditing: true,
        isExpanded: true,
      })
    );
  };

  const updateReviewAfterSubmit = (
    index: number,
    persistedNewReview: InterviewReviewType
  ): void => {
    setReviews((prev) =>
      prev.map((review, reviewIndex) => {
        if (index !== reviewIndex) {
          return review;
        }

        return {
          ...omit(persistedNewReview, ['interview', 'score']),
          competencies: formatCompetencies({
            scores: persistedNewReview.score,
            competencies: persistedNewReview.competencies,
          }),
          note: persistedNewReview.note,
          isEditing: false,
          isExpanded: false,
        };
      })
    );
  };

  const maybeOpenAiOverviewModal = (persistedReview: InterviewReviewType): void => {
    if (!persistedReview.aiGeneratedOverview) return;

    setAiOverviewModalState({
      isOpen: true,
      reviewId: persistedReview.id,
      overview: persistedReview.aiGeneratedOverview,
    });
  };

  const onCancelAddNewInterviewReview = (interviewReviewIndex: number): void => {
    setReviews((prev) =>
      prev.filter((review, index) => {
        return interviewReviewIndex !== index;
      })
    );
  };

  const formatCompetenciesWithScoresToPersist = (
    competencies?: TechnicalValidationCompetency[]
  ): any[] => {
    if (!competencies) {
      return [];
    }

    const currentCompetencies = competencies.map((competency) => {
      return {
        ...competency,
        scores: competency.scores.map((score) => {
          const value = score.scoreValue || getInputNumberedValue(score.value);

          // The maxScoreValue for boolean inputs is the score value when the input is 'Yes'
          const normalizedValue = getScoreValueBasedOnMaxScoreValue({
            scoreValue: value,
            maxScoreValue: score.maxScoreValue,
            dropdownOptions: score.options,
          });

          return {
            id: score.id,
            scoreType: score.name as InterviewScoreEnum,
            score: normalizedValue * competency.weight, // Apply weight when sending to API
            technicalChallengeCompetencyScoreId: score.technicalChallengeCompetencyScoreId,
          };
        }),
      };
    });

    return currentCompetencies;
  };

  const onAddNewInterviewReview = async (
    index: number,
    newReview: CustomReviewType
  ): Promise<boolean> => {
    const { id } = newReview;
    const isAlreadyCreated = !!id;
    const actionsVerb = isAlreadyCreated ? 'updated' : 'added';

    const methodToCall = isAlreadyCreated
      ? updateInterviewReviewMutation
      : addInterviewReviewMutation;

    const reviewWithScores = {
      ...newReview,
      competencies: formatCompetenciesWithScoresToPersist(newReview.competencies),
    };

    try {
      const reviewWithOmittedFields = omit(reviewWithScores, [
        'isEditing',
        'isExpanded',
        'overviewNote',
        !isAlreadyCreated ? 'id' : '',
      ]);

      const persistedReview = (await methodToCall.mutateAsync({
        interviewId: interview.id,
        interviewReviewId: id,
        ...reviewWithOmittedFields,
      })) as InterviewReviewType;

      updateReviewAfterSubmit(index, persistedReview);
      onRefetchInterview();
      toast.showSuccessFeedback(`The review has been ${actionsVerb} to the interview.`);
      maybeOpenAiOverviewModal(persistedReview);
    } catch {
      const actionContinuousVerb = actionsVerb.replace('ed', 'ing');

      toast.showErrorFeedback(
        `There was a problem to ${actionContinuousVerb} the review to the interview.`
      );
    }

    return methodToCall.isSuccess;
  };

  const onDeleteInterviewReview = async (interviewReviewId: string): Promise<boolean> => {
    try {
      await deleteInterviewReviewMutation.mutateAsync({
        interviewId: interview.id,
        interviewReviewId,
      });

      setReviews((prev) =>
        prev.filter((review) => {
          return interviewReviewId !== review.id;
        })
      );
      onRefetchInterview();
      toast.showSuccessFeedback('The review has been deleted of the interview.');
    } catch {
      toast.showErrorFeedback('There was a problem to delete the review of the interview.');
    }

    return deleteInterviewReviewMutation.isSuccess;
  };

  const isActionLoading =
    addInterviewReviewMutation.isLoading ||
    updateInterviewReviewMutation.isLoading ||
    deleteInterviewReviewMutation.isLoading;

  return (
    <>
      <TechnicalValidationReviewsManagerComponent
        isActionLoading={isActionLoading}
        canAddNewReview={!!reviews.length}
        canAddFirstReview={canAddFirstReview}
        reviews={reviews}
        finalScore={interview.finalScore}
        reviewsResult={interview.reviewsResult}
        onAddNewInterviewReview={onAddNewInterviewReview}
        onAddNewReview={onAddNewReview}
        onDeleteInterviewReview={onDeleteInterviewReview}
        onCancelAddNewInterviewReview={onCancelAddNewInterviewReview}
      />

      {aiOverviewModalState && (
        <AiOverviewModal
          isOpen={aiOverviewModalState.isOpen}
          initialOverview={aiOverviewModalState.overview}
          isSubmitting={updateAiOverviewMutation.isLoading}
          onSubmit={async (updatedOverview) => {
            try {
              await updateAiOverviewMutation.mutateAsync({
                interviewId: interview.id,
                interviewReviewId: aiOverviewModalState.reviewId,
                aiGeneratedOverview: updatedOverview,
              });
              onRefetchInterview();
              toast.showSuccessFeedback('The AI-generated overview has been updated.');
              setAiOverviewModalState(null);
            } catch {
              toast.showErrorFeedback('There was a problem updating the AI-generated overview.');
            }
          }}
        />
      )}
    </>
  );
}
